import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { Box, Spinner, Typography } from '@locus-taxy/disco-ui';
import {
  useTranslation,
  type TranslateFunction,
} from '@locus-taxy/encore-i18n-toolkit';

import EntityGridView from './components/EntityGridView';
import CustomAppPage from './components/CustomAppPage';
import RouteWrapper from './components/RouteWrapper';
import DiscoAppShell from './components/layout/DiscoAppShell';
import DimensionValuePicker from './components/layout/DimensionValuePicker';
import { DashboardNavigate } from './components/DashboardNavigate';
import { Routes, Route, useParams } from 'react-router-dom';
import { EntityDetails } from './components/EntityDetails';
import UserDetails from './components/UserDetails';
import {
  useClientId,
  hasSubdomainClientId,
  extractClientIdFromSubdomain,
} from './hooks/useClientId';
import { useAnalytics } from './hooks/useAnalytics';
import SettingsPage from './components/SettingsPage';
import AnalyticsPage from './components/AnalyticsPage';
import { OutboundContainer } from './OutboundContainer';
import { RoutePath } from './types/categories';
import { buildClientPrefixedPath } from './utils/url';
import { NotFoundPage } from './components/NotFoundPage';
import { useLogoutWithO11y } from './hooks/useLogoutWithO11y';
import useConfigureLocale from './hooks/useConfigureLocale';
import { useAppNavigation } from './hooks/useAppNavigation';
import { useClientMetadata } from './api';
import { useClientIdUrlCorrection } from './hooks/useClientIdUrlCorrection';
import { useDimensionValueGate } from './hooks/useDimensionValueGate';
import { isEmbeddedWebView } from './api/network/nativeBridge';
import type { Navigation } from './types/navigation';

function EntityRouteWrapper() {
  // `usecase` is undefined on the default entity route (no `:usecase` segment)
  // and set on the DP-3570 usecase route — the same wrapper serves both.
  const { entityName, usecase } = useParams();
  return (
    <RouteWrapper>
      <EntityGridView selectedEntity={entityName} usecase={usecase} />
    </RouteWrapper>
  );
}

function CustomAppRouteWrapper() {
  const { appName } = useParams();
  // Remount CustomAppPage when navigating between different appNames so the
  // Module Federation runtime tears down and re-fetches the new manifest.
  return (
    <RouteWrapper>
      <CustomAppPage key={appName} />
    </RouteWrapper>
  );
}

function AppRegistryRouteWrapper() {
  const { appName } = useParams();
  return (
    <RouteWrapper>
      <EntityGridView selectedEntity={appName} />
    </RouteWrapper>
  );
}

function AnalyticsRouteWrapper() {
  return (
    <RouteWrapper>
      <AnalyticsPage />
    </RouteWrapper>
  );
}

// Root redirect component with delay to allow Auth0 callback to complete
function RootRedirect({ toUrl }: Readonly<{ toUrl: string }>) {
  return <DashboardNavigate to={toUrl} replace />;
}

// No dynamic module-based routes; using static RoutePath-based routes only

// Renders `children` inside the app shell appropriate for the current
// environment. Extracted out of `App()` so the branch doesn't count toward
// that function's cyclomatic complexity (SonarQube typescript:S1541).
function renderAppShell(
  navigation: Navigation,
  bottomNavigation: Navigation,
  children: ReactNode
) {
  if (isEmbeddedWebView()) {
    // No sidebar/header chrome in a native WebView. Reproduce the height
    // context DiscoAppShell's SidebarProvider normally supplies (see
    // @locus-taxy/disco-ui's SidebarProvider: "flex h-svh w-full
    // overflow-hidden") so routedContent's flex-1/min-h-0 still resolve.
    return (
      <Box className="flex h-svh w-full flex-col overflow-hidden">
        {children}
      </Box>
    );
  }
  return (
    <DiscoAppShell navigation={navigation} bottomNavigation={bottomNavigation}>
      {children}
    </DiscoAppShell>
  );
}

/**
 * The subdomain-URL structure fetches client metadata up front and blocks on it.
 * Returns the blocking view, or `null` to carry on rendering.
 */
function renderClientMetadataGate({
  active,
  isLoading,
  isError,
  t,
}: {
  active: boolean;
  isLoading: boolean;
  isError: boolean;
  t: TranslateFunction;
}) {
  if (!active) {
    return null;
  }
  if (isLoading) {
    return (
      <Box className="flex h-screen items-center justify-center">
        <Spinner />
      </Box>
    );
  }
  if (!isError) {
    return null;
  }
  // The error toast already comes from useClientMetadata; this blocks the app.
  return (
    <Box className="flex h-screen flex-col items-center justify-center gap-4">
      <Typography variant="h6" color="destructive">
        {t(
          'dashboard.app.clientMetadataLoadError',
          'Failed to load client metadata. Please refresh the page.'
        )}
      </Typography>
    </Box>
  );
}

/** Log the user out once when there is no client to render for. */
function useLogoutWhenClientMissing(clientId: string | null | undefined) {
  const logoutWithO11y = useLogoutWithO11y();
  const hasTriggeredLogoutRef = useRef(false);

  useEffect(() => {
    if (clientId || hasTriggeredLogoutRef.current) {
      return;
    }
    hasTriggeredLogoutRef.current = true;
    void (async () => {
      try {
        await logoutWithO11y({
          logoutParams: { returnTo: window.location.origin },
        });
      } catch {
        // ignore logout errors, sync or async
      }
    })();
  }, [clientId, logoutWithO11y]);
}

/**
 * The shell, with the first-login dimension picker layered over it.
 *
 * The picker overlays the real dashboard rather than replacing it, so it reads
 * as part of the product. `inert` stops the user tabbing into the shell to
 * escape the gate, and is what makes the dialog's `aria-modal` honest.
 *
 * **The routed content mounts underneath, always.** An earlier revision swapped
 * it for a placeholder while the picker was open, reasoning that mounting it
 * would run every entity grid against a scope the actor had not chosen yet.
 * That conflated two different things: deferring a *fetch* and refusing to
 * *route*. `routedContent` is the whole `<Routes>` tree, so swapping it meant no
 * route matched at all while the question was on screen — every deep link
 * landed on nothing, and a custom-app MFE never issued the manifest request
 * that its mount is what triggers (DP-4121 field report: `/apps/{name}` deep
 * links showed zero manifest calls). A grid recovered invisibly on the next
 * click, which is why only the MFE case was noticed.
 *
 * The trade-off that swap was buying is real but small: while the question is
 * open, a grid underneath fetches at the actor's full reach and refetches once
 * the answer lands. Those rows are all rows the server already curates to this
 * actor, they sit behind an `inert` shell under a modal, and the fix for the
 * waste is to defer the *query* (see the `enabled` note in the record-access
 * hooks), never to withhold the route.
 */
function GatedAppShell({
  gate,
  navigation,
  bottomNavigation,
  routedContent,
}: Readonly<{
  gate: ReturnType<typeof useDimensionValueGate>;
  navigation: Navigation;
  bottomNavigation: Navigation;
  routedContent: ReactNode;
}>) {
  // The narrowed status, not a boolean: a boolean would not narrow the union,
  // so `options`/`label` below would be unreachable.
  const pickerStatus = gate.status.kind === 'picker' ? gate.status : null;

  return (
    <>
      <Box inert={pickerStatus ? true : undefined} className="contents">
        {renderAppShell(navigation, bottomNavigation, routedContent)}
      </Box>
      {pickerStatus && (
        <DimensionValuePicker
          options={pickerStatus.options}
          label={pickerStatus.label}
          onPick={gate.pick}
        />
      )}
    </>
  );
}

export default function App() {
  const { t } = useTranslation();
  const clientId = useClientId();
  useLogoutWhenClientMissing(clientId);

  const hostname = window.location.hostname;
  const hasSubdomain = hasSubdomainClientId(hostname);
  const subdomainClientId = extractClientIdFromSubdomain(hostname);

  // URL correction: For new URL structure, if route clientId differs from subdomain clientId, redirect
  useClientIdUrlCorrection();

  // Auto-fetch client metadata for new URL structure - block app if it fails
  const { isLoading: isMetadataLoading, isError: isMetadataError } =
    useClientMetadata(
      hasSubdomain && subdomainClientId ? subdomainClientId : null,
      {
        enabled: hasSubdomain && Boolean(subdomainClientId),
      }
    );

  const { navigation, bottomNavigation, firstLeafPath, isModulesLoading } =
    useAppNavigation(clientId);

  useAnalytics({ clientId });

  // Configure locale + gate first paint until its catalog is loaded
  const { localeReady } = useConfigureLocale();

  // Record-access scope resolver (DP-3188). Called here with the other hooks —
  // rules-of-hooks forbid it below the early returns — but its branches are
  // read *after* the `localeReady` guard below, so the picker can never paint
  // against an unloaded locale catalog.
  const gate = useDimensionValueGate();

  const metadataGate = renderClientMetadataGate({
    active: Boolean(hasSubdomain && subdomainClientId),
    isLoading: isMetadataLoading,
    isError: isMetadataError,
    t,
  });
  if (metadataGate) {
    return metadataGate;
  }

  // Holds until the active-locale catalog is loaded, so disco-ui and memoized
  // form labels render translated on first paint (useConfigureLocale).
  //
  // The record-access gate is deliberately NOT part of this condition, and must
  // never become part of it again. `routedContent` below *is* the `<Routes>`
  // tree, so anything that withholds it withholds routing itself: a deep link
  // resolves to nothing, and a route whose mount has a side effect — a
  // custom-app MFE fetching its federation manifest — never performs it. The
  // gate answers "which values is this actor looking at", which is a question
  // about *data*, and it may not be answered by suppressing *routing*.
  // See `App.routing.test.tsx`, which pins this for every route in the table.
  if (!localeReady) {
    return (
      <Box className="flex h-screen items-center justify-center">
        <Spinner />
      </Box>
    );
  }

  const routedContent = (
    <Box className="flex min-h-0 flex-1 flex-col">
      <Routes>
        <Route
          path={`/clientId/:clientId${RoutePath.ENTITIES}/:entityName`}
          element={<EntityRouteWrapper />}
        />
        <Route
          path={`/clientId/:clientId${RoutePath.ENTITIES}/:entityName/:entityId`}
          element={<EntityDetails />}
        />

        {/*
         * Usecase pages (DP-3570). The `/uc/` marker structurally disambiguates
         * the usecase list route from the default details route (`:entityId`)
         * and keeps the entity path param concrete so backend authorization is
         * unchanged. These routes render purely from route params — no
         * dependency on the /modules payload containing a matching leaf, so a
         * deep link works even when the user's role lacks the nav leaf.
         */}
        <Route
          path={`/clientId/:clientId${RoutePath.ENTITIES}/:entityName/uc/:usecase`}
          element={<EntityRouteWrapper />}
        />
        <Route
          path={`/clientId/:clientId${RoutePath.ENTITIES}/:entityName/uc/:usecase/:entityId`}
          element={<EntityDetails />}
        />

        <Route
          path={`/clientId/:clientId/entities/:entityName/:entityId/outbound`}
          element={<OutboundContainer />}
        />
        <Route
          path={`/clientId/:clientId${RoutePath.APP_REGISTRY}/:appName`}
          element={<AppRegistryRouteWrapper />}
        />
        <Route
          // Splat so deep links into a custom app (e.g. /apps/entity-view-config/new)
          // match the host route and mount the MFE, which owns its internal routing.
          path={`/clientId/:clientId${RoutePath.APPS}/:appName/*`}
          element={<CustomAppRouteWrapper />}
        />
        <Route
          path={`/clientId/:clientId${RoutePath.SETTINGS}`}
          element={<SettingsPage />}
        />
        <Route
          path={`/clientId/:clientId${RoutePath.ACCOUNT}`}
          element={<UserDetails />}
        />
        <Route
          path={`/clientId/:clientId${RoutePath.ANALYTICS}/*`}
          element={<AnalyticsRouteWrapper />}
        />
        <Route
          path="/"
          element={
            <DashboardNavigate
              to={buildClientPrefixedPath('/', clientId)}
              replace
            />
          }
        />
        <Route
          path="clientId/:clientId"
          element={
            !isModulesLoading ? (
              <RootRedirect
                toUrl={buildClientPrefixedPath(firstLeafPath ?? '/', clientId)}
              />
            ) : (
              <Box className="flex h-screen items-center justify-center">
                <Spinner />
              </Box>
            )
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Box>
  );

  return (
    <GatedAppShell
      gate={gate}
      navigation={navigation}
      bottomNavigation={bottomNavigation}
      routedContent={routedContent}
    />
  );
}
